import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DictationButton } from "@/components/DictationButton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Sélecteur de demande réutilisable
function useDemandes() {
  const [demandes, setDemandes] = useState([]);
  useEffect(() => { api.get("/demandes").then((r) => setDemandes(r.data)).catch(() => {}); }, []);
  return demandes;
}
function useUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([])); }, []);
  return users;
}

function CreateDialog({ label, testid, children, onSubmit }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={testid} style={{ background: "#0F2942" }}><Plus className="h-4 w-4 mr-1" />{label}</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
        {children}
        <DialogFooter><Button onClick={async () => { const ok = await onSubmit(); if (ok !== false) setOpen(false); }} style={{ background: "#0284C7" }}>Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DemandeSelect({ demandes, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="partie-demande-select"><SelectValue placeholder="Demande liée" /></SelectTrigger>
      <SelectContent className="bg-white">
        {demandes.map((d) => <SelectItem key={d.id} value={d.id}>{d.numero} — {d.type_examen}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ===== Partie B =====
function PartieB() {
  const demandes = useDemandes(); const users = useUsers();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ demande_id: "", destinataire_id: "", question: "" });
  const load = () => api.get("/parties/b/consultations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/parties/b/consultations", f); toast.success("Consultation créée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  return (
    <Section title="Partie B — Consultations internes" action={
      <CreateDialog label="Nouvelle consultation" testid="new-consultation-button" onSubmit={submit}>
        <div className="space-y-3">
          <DemandeSelect demandes={demandes} value={f.demande_id} onChange={(v) => setF({ ...f, demande_id: v })} />
          <Select value={f.destinataire_id} onValueChange={(v) => setF({ ...f, destinataire_id: v })}>
            <SelectTrigger><SelectValue placeholder="Consultant destinataire" /></SelectTrigger>
            <SelectContent className="bg-white">
              {users.filter((u) => ["pathologiste", "chef_service", "pathologiste_distant"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea placeholder="Question / sujet" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
          <DictationButton value={f.question || ""} onChange={(v) => setF((x) => ({ ...x, question: v }))} testId="dictation-consultation" />
        </div>
      </CreateDialog>
    }>
      <Table className="clin-table"><TableHeader><TableRow><TableHead>Demande</TableHead><TableHead>Destinataire</TableHead><TableHead>Question</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => <TableRow key={r.id} className="h-9"><TableCell className="font-mono text-xs">{r.numero}</TableCell><TableCell>{r.destinataire_nom}</TableCell><TableCell className="text-xs">{r.question}</TableCell><TableCell><Badge variant="outline">{r.statut}</Badge></TableCell></TableRow>)}</TableBody></Table>
    </Section>
  );
}

// ===== Partie C =====
function ResultatBadge({ value }) {
  const map = {
    confirme: "bg-emerald-50 text-emerald-700 border-emerald-200",
    infirme: "bg-red-50 text-red-700 border-red-200",
    nuance: "bg-amber-50 text-amber-800 border-amber-200",
  };
  const label = { confirme: "Confirme", infirme: "Infirme", nuance: "Nuance" };
  return <Badge variant="outline" className={map[value] || ""}>{label[value] || value}</Badge>;
}

function RendreAvisDialog({ avis, onDone }) {
  const [open, setOpen] = useState(false);
  const [resultat, setResultat] = useState("confirme");
  const [commentaire, setCommentaire] = useState("");
  const submit = async () => {
    try {
      await api.post(`/parties/c/avis/${avis.id}/rendre`, { resultat, commentaire });
      toast.success("Avis rendu et signé"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`rendre-avis-${avis.numero}`}>Rendre l'avis</Button></DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Rendre l'avis — {avis.numero} ({avis.ordre}ᵉ)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={resultat} onValueChange={setResultat}>
            <SelectTrigger data-testid="rendre-resultat-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="confirme">Confirme le diagnostic</SelectItem>
              <SelectItem value="infirme">Infirme le diagnostic</SelectItem>
              <SelectItem value="nuance">Nuance le diagnostic</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Commentaire de l'expert" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          <DictationButton value={commentaire} onChange={setCommentaire} testId="dictation-avis-expert" />
        </div>
        <DialogFooter><Button onClick={submit} style={{ background: "#0284C7" }} data-testid="rendre-avis-submit">Signer l'avis</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsensusDialog({ demandeId, numero, onDone }) {
  const [open, setOpen] = useState(false);
  const [conclusion, setConclusion] = useState("");
  const submit = async () => {
    try {
      await api.post("/parties/c/avis/consensus", { demande_id: demandeId, conclusion });
      toast.success("Consensus signé par le chef"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="h-7 text-xs" style={{ background: "#DC2626" }} data-testid={`consensus-${numero}`}>Consensus</Button></DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Consensus du chef — {numero}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Les avis d'experts divergent. En tant que chef de service, rendez la décision finale.</p>
        <Textarea placeholder="Conclusion consensuelle" value={conclusion} onChange={(e) => setConclusion(e.target.value)} />
        <DictationButton value={conclusion} onChange={setConclusion} testId="dictation-consensus" />
        <DialogFooter><Button onClick={submit} style={{ background: "#059669" }} data-testid="consensus-submit">Signer le consensus</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartieC() {
  const { user } = useAuth();
  const demandes = useDemandes();
  const [experts, setExperts] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ demande_id: "", expert_id: "", motif: "", cmq: false });
  const isChef = ["chef_service", "directeur_medical"].includes(user?.role);
  const canVerifyCmq = ["admin_technique", "chef_service", "responsable_qualite"].includes(user?.role);
  const loadExperts = () => api.get("/parties/c/experts").then((r) => setExperts(r.data)).catch(() => setExperts([]));
  const load = () => api.get("/parties/c/avis").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); loadExperts(); }, []);
  const submit = async () => {
    try { await api.post("/parties/c/avis", f); toast.success("Avis demandé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const verifierCmq = async (uid) => {
    const numero = window.prompt("Numéro d'ordre CMQ de l'expert :");
    if (!numero) return;
    try { await api.post(`/parties/c/experts/${uid}/verifier-cmq`, { numero_ordre: numero }); toast.success("Expert vérifié CMQ"); loadExperts(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const byDemande = {};
  rows.forEach((r) => { (byDemande[r.demande_id] = byDemande[r.demande_id] || []).push(r); });

  return (
    <Section title="Partie C — Avis du Pathologiste Expert (chaîne · consensus · CMQ)" action={
      <CreateDialog label="Demander un avis" testid="new-avis-button" onSubmit={submit}>
        <div className="space-y-3">
          <DemandeSelect demandes={demandes} value={f.demande_id} onChange={(v) => setF({ ...f, demande_id: v })} />
          <Select value={f.expert_id} onValueChange={(v) => setF({ ...f, expert_id: v })}>
            <SelectTrigger data-testid="avis-expert-select"><SelectValue placeholder="Pathologiste Expert" /></SelectTrigger>
            <SelectContent className="bg-white">{experts.map((u) => <SelectItem key={u.id} value={u.id}>{u.nom}{u.cmq_verifie ? " ✓CMQ" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Motif de l'avis" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} />
          <DictationButton value={f.motif || ""} onChange={(v) => setF((x) => ({ ...x, motif: v }))} testId="dictation-avis-motif" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.cmq} onChange={(e) => setF({ ...f, cmq: e.target.checked })} /> Vérification CMQ requise</label>
        </div>
      </CreateDialog>
    }>
      {canVerifyCmq && experts.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="cmq-registry">
          <span className="text-xs text-slate-500 self-center mr-1">Registre CMQ :</span>
          {experts.map((u) => (
            <div key={u.id} className="flex items-center gap-2 text-xs border border-slate-200 rounded px-2 py-1">
              <span>{u.nom}</span>
              {u.cmq_verifie
                ? <Badge className="bg-emerald-600 text-[10px]">CMQ {u.numero_ordre_cmq}</Badge>
                : <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => verifierCmq(u.id)} data-testid={`verify-cmq-${u.id}`}>Vérifier CMQ</Button>}
            </div>
          ))}
        </div>
      )}
      <Table className="clin-table"><TableHeader><TableRow>
        <TableHead>Demande</TableHead><TableHead>Expert</TableHead><TableHead>Ordre</TableHead>
        <TableHead>Résultat</TableHead><TableHead>Statut</TableHead><TableHead></TableHead>
      </TableRow></TableHeader>
        <TableBody>{rows.map((r) => {
          const mine = r.expert_id === user?.id;
          const grp = (byDemande[r.demande_id] || []).filter((a) => a.rendu);
          const divergence = grp.length >= 2 && new Set(grp.map((a) => a.resultat)).size > 1 && r.statut !== "consensus";
          return (
            <TableRow key={r.id} className="h-9">
              <TableCell className="font-mono text-xs">{r.numero}</TableCell>
              <TableCell className="text-sm">{r.expert_nom} {r.cmq && (r.expert_cmq_verifie ? <span className="text-emerald-600" title="CMQ vérifié">✓</span> : <span className="text-amber-600" title="CMQ non vérifié">⚠</span>)}</TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{r.ordre}ᵉ avis</Badge></TableCell>
              <TableCell>{r.resultat ? <ResultatBadge value={r.resultat} /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
              <TableCell><Badge variant="outline" className={r.statut === "divergence" ? "bg-amber-50 text-amber-800 border-amber-200" : r.statut === "consensus" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : ""}>{r.statut}</Badge></TableCell>
              <TableCell><div className="flex gap-1">
                {!r.rendu && (mine || isChef) && <RendreAvisDialog avis={r} onDone={load} />}
                {r.rendu && divergence && isChef && <ConsensusDialog demandeId={r.demande_id} numero={r.numero} onDone={load} />}
              </div></TableCell>
            </TableRow>
          );
        })}</TableBody></Table>
    </Section>
  );
}

// ===== Partie D =====
function AbsenceDialog({ pathologistes, onDone }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState({ pathologiste_id: "", date_debut: "", date_fin: "", motif: "" });
  const submit = async () => {
    try { await api.post("/parties/d/absences", a); toast.success("Absence déclarée"); setOpen(false); onDone && onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-absence-button">Déclarer une absence</Button></DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Déclarer une absence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={a.pathologiste_id} onValueChange={(v) => setA({ ...a, pathologiste_id: v })}>
            <SelectTrigger data-testid="absence-patho-select"><SelectValue placeholder="Pathologiste" /></SelectTrigger>
            <SelectContent className="bg-white">{pathologistes.map((u) => <SelectItem key={u.id} value={u.id}>{u.nom}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Début</Label><Input type="date" value={a.date_debut} onChange={(e) => setA({ ...a, date_debut: e.target.value })} /></div>
            <div><Label>Fin</Label><Input type="date" value={a.date_fin} onChange={(e) => setA({ ...a, date_fin: e.target.value })} /></div>
          </div>
          <Input placeholder="Motif (congé, formation…)" value={a.motif} onChange={(e) => setA({ ...a, motif: e.target.value })} />
          <DictationButton value={a.motif || ""} onChange={(v) => setA((x) => ({ ...x, motif: v }))} testId="dictation-absence-motif" />
        </div>
        <DialogFooter><Button onClick={submit} style={{ background: "#0284C7" }} data-testid="absence-submit">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EchangeDialog({ gardes, onDone }) {
  const [open, setOpen] = useState(false);
  const [g1, setG1] = useState(""); const [g2, setG2] = useState("");
  const optLabel = (g) => `${g.pathologiste_nom} · ${g.date_debut} (${g.type_garde})`;
  const submit = async () => {
    if (!g1 || !g2 || g1 === g2) return toast.error("Choisissez deux gardes différentes");
    try {
      await api.post("/parties/d/gardes/echanger", { garde_id_1: g1, garde_id_2: g2 });
      toast.success("Gardes échangées — pensez à republier l'horaire");
      setOpen(false); setG1(""); setG2(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-echange-button">Échanger deux gardes</Button></DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Échanger deux gardes</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Première garde</Label>
            <Select value={g1} onValueChange={setG1}>
              <SelectTrigger data-testid="echange-g1-select"><SelectValue placeholder="Garde A" /></SelectTrigger>
              <SelectContent className="bg-white">{gardes.map((g) => <SelectItem key={g.id} value={g.id}>{optLabel(g)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Seconde garde</Label>
            <Select value={g2} onValueChange={setG2}>
              <SelectTrigger data-testid="echange-g2-select"><SelectValue placeholder="Garde B" /></SelectTrigger>
              <SelectContent className="bg-white">{gardes.map((g) => <SelectItem key={g.id} value={g.id}>{optLabel(g)}</SelectItem>)}</SelectContent>
            </Select></div>
          <p className="text-xs text-slate-500">Les pathologistes assignés seront permutés et l'horaire repassera en statut « planifié » pour republication.</p>
        </div>
        <DialogFooter><Button onClick={submit} style={{ background: "#0284C7" }} data-testid="echange-submit">Échanger</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartieD() {
  const [pathologistes, setPathologistes] = useState([]);
  const [rows, setRows] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [echanges, setEchanges] = useState([]);
  const [statsGarde, setStatsGarde] = useState([]);
  const [f, setF] = useState({ pathologiste_id: "", date_debut: "", date_fin: "", type_garde: "jour" });
  const [suggestion, setSuggestion] = useState(null);
  const load = () => api.get("/parties/d/gardes").then((r) => setRows(r.data)).catch(() => {});
  const loadRefs = () => {
    api.get("/parties/d/pathologistes").then((r) => setPathologistes(r.data)).catch(() => setPathologistes([]));
    api.get("/parties/d/absences").then((r) => setAbsences(r.data)).catch(() => setAbsences([]));
    api.get("/parties/d/echanges").then((r) => setEchanges(r.data)).catch(() => setEchanges([]));
    api.get("/parties/d/gardes/statistiques").then((r) => setStatsGarde(r.data)).catch(() => setStatsGarde([]));
  };
  useEffect(() => { load(); loadRefs(); }, []);
  const submit = async () => {
    try { await api.post("/parties/d/gardes", f); toast.success("Garde planifiée"); setSuggestion(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const publier = async () => { try { await api.post("/parties/d/gardes/publier"); toast.success("Horaire publié"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const envoyerRappels = async () => { try { const r = await api.post("/parties/d/gardes/rappels"); toast.success(`${r.data.envoyes}/${r.data.total} rappel(s) envoyé(s) pour demain`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const suggerer = async () => {
    if (!f.date_debut) return toast.error("Choisissez d'abord la date de début");
    try {
      const r = await api.post("/parties/d/gardes/suggerer", { date_debut: f.date_debut, date_fin: f.date_fin || f.date_debut, type_garde: f.type_garde });
      setF((prev) => ({ ...prev, pathologiste_id: r.data.pathologiste_id }));
      setSuggestion(r.data);
      toast.success(`Suggestion : ${r.data.pathologiste_nom} (${r.data.compteur} garde(s))`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Section title="Partie D — Horaires de garde (auto-suggestion, équité)" action={
      <div className="flex flex-wrap gap-2">
        <AbsenceDialog pathologistes={pathologistes} onDone={loadRefs} />
        <EchangeDialog gardes={rows} onDone={load} />
        <Button size="sm" variant="outline" data-testid="export-ics-button"
                onClick={() => downloadFile("/parties/d/gardes/export.ics", "gardes-path-intelligent.ics").catch(() => toast.error("Export ICS impossible"))}>
          Exporter (.ics)
        </Button>
        <Button size="sm" variant="outline" onClick={publier} data-testid="publish-gardes-button">Publier l'horaire</Button>
        <Button size="sm" variant="outline" onClick={envoyerRappels} data-testid="rappels-garde-button">Envoyer les rappels (demain)</Button>
        <CreateDialog label="Planifier une garde" testid="new-garde-button" onSubmit={submit}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Début</Label><Input type="date" data-testid="garde-debut-input" value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} /></div>
              <div><Label>Fin</Label><Input type="date" value={f.date_fin} onChange={(e) => setF({ ...f, date_fin: e.target.value })} /></div>
            </div>
            <Button type="button" size="sm" variant="outline" className="w-full" onClick={suggerer} data-testid="suggerer-garde-button" style={{ borderColor: "#0284C7", color: "#0284C7" }}>
              ✨ Suggérer automatiquement (compteur le plus bas)
            </Button>
            {suggestion && (
              <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2 text-sky-800" data-testid="suggestion-info">
                <strong>{suggestion.pathologiste_nom}</strong> — {suggestion.compteur} garde(s). {suggestion.raison}.
                {(suggestion.exclus_veille.length > 0 || suggestion.exclus_absences.length > 0) &&
                  <div className="mt-1 text-[11px] text-slate-500">Exclus : {suggestion.exclus_veille.length} (veille), {suggestion.exclus_absences.length} (absence).</div>}
              </div>
            )}
            <Select value={f.pathologiste_id} onValueChange={(v) => setF({ ...f, pathologiste_id: v })}>
              <SelectTrigger data-testid="garde-patho-select"><SelectValue placeholder="Pathologiste" /></SelectTrigger>
              <SelectContent className="bg-white">{pathologistes.map((u) => <SelectItem key={u.id} value={u.id}>{u.nom}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CreateDialog>
      </div>
    }>
      {absences.length > 0 && (
        <div className="mb-3 text-xs text-slate-500">
          <span className="font-medium">Absences :</span> {absences.map((a) => `${a.pathologiste_nom} (${a.date_debut}→${a.date_fin})`).join(" · ")}
        </div>
      )}
      <Table className="clin-table"><TableHeader><TableRow><TableHead>Pathologiste</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead><TableHead>Type</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => <TableRow key={r.id} className="h-9"><TableCell>{r.pathologiste_nom}</TableCell><TableCell className="text-xs">{r.date_debut}</TableCell><TableCell className="text-xs">{r.date_fin}</TableCell><TableCell>{r.type_garde}</TableCell><TableCell><Badge variant="outline">{r.statut}</Badge></TableCell></TableRow>)}</TableBody></Table>
      {echanges.length > 0 && (
        <div className="mt-5" data-testid="echanges-historique">
          <h3 className="font-head text-sm font-semibold text-slate-700 mb-2">Historique des échanges de garde</h3>
          <Table className="clin-table"><TableHeader><TableRow><TableHead>Échange</TableHead><TableHead>Effectué par</TableHead><TableHead>Le</TableHead></TableRow></TableHeader>
            <TableBody>{echanges.map((h) => (
              <TableRow key={h.id} className="h-9">
                <TableCell className="text-xs">{h.pathologiste_1} ({h.date_1}) ⇄ {h.pathologiste_2} ({h.date_2})</TableCell>
                <TableCell className="text-xs">{h.effectue_par}</TableCell>
                <TableCell className="text-xs">{h.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
              </TableRow>
            ))}</TableBody></Table>
        </div>
      )}
      {statsGarde.length > 0 && (
        <div className="mt-5" data-testid="gardes-equite">
          <h3 className="font-head text-sm font-semibold text-slate-700 mb-2">Équité des gardes (nombre par pathologiste)</h3>
          <Table className="clin-table"><TableHeader><TableRow><TableHead>Pathologiste</TableHead><TableHead>Nombre de gardes</TableHead></TableRow></TableHeader>
            <TableBody>{statsGarde.map((s) => (
              <TableRow key={s.pathologiste_id || s.pathologiste_nom} className="h-9">
                <TableCell className="text-sm">{s.pathologiste_nom}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono">{s.nombre}</Badge></TableCell>
              </TableRow>
            ))}</TableBody></Table>
        </div>
      )}
    </Section>
  );
}

// ===== Partie E =====
function PartieE() {
  const demandes = useDemandes();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ demande_id: "", motif: "cancer" });
  const load = () => api.get("/parties/e/double-validation").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/parties/e/double-validation", f); toast.success("Double validation demandée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const valider = async (id) => { try { await api.post(`/parties/e/double-validation/${id}/valider`); toast.success("2ᵉ validation enregistrée"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Section title="Partie E — Double validation" action={
      <CreateDialog label="Demander une 2ᵉ lecture" testid="new-double-validation-button" onSubmit={submit}>
        <div className="space-y-3">
          <DemandeSelect demandes={demandes} value={f.demande_id} onChange={(v) => setF({ ...f, demande_id: v })} />
          <Select value={f.motif} onValueChange={(v) => setF({ ...f, motif: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="cancer">Cancer</SelectItem><SelectItem value="pediatrie">Pédiatrie</SelectItem><SelectItem value="rare">Cas rare</SelectItem></SelectContent>
          </Select>
        </div>
      </CreateDialog>
    }>
      <Table className="clin-table"><TableHeader><TableRow><TableHead>Demande</TableHead><TableHead>Motif</TableHead><TableHead>1er validateur</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => <TableRow key={r.id} className="h-9"><TableCell className="font-mono text-xs">{r.numero}</TableCell><TableCell><Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{r.motif}</Badge></TableCell><TableCell className="text-xs">{r.premier_validateur}</TableCell><TableCell><Badge variant="outline">{r.statut}</Badge></TableCell><TableCell>{r.statut !== "validee" && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => valider(r.id)} data-testid={`valider-${r.numero}`}>Valider (2ᵉ)</Button>}</TableCell></TableRow>)}</TableBody></Table>
    </Section>
  );
}

// ===== Partie F =====
function PartieF() {
  const demandes = useDemandes();
  const [rows, setRows] = useState([]); const [codes, setCodes] = useState({});
  const [f, setF] = useState({ demande_id: "", code: "IHC" });
  const load = () => api.get("/parties/f/facturation").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/parties/f/codes").then((r) => setCodes(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try { await api.post("/parties/f/facturation", { demande_id: f.demande_id, lignes: [{ code: f.code, description: f.code }] }); toast.success("Facturation créée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  return (
    <Section title="Partie F — Facturation L4E" action={
      <CreateDialog label="Nouvelle facturation" testid="new-facturation-button" onSubmit={submit}>
        <div className="space-y-3">
          <DemandeSelect demandes={demandes} value={f.demande_id} onChange={(v) => setF({ ...f, demande_id: v })} />
          <Select value={f.code} onValueChange={(v) => setF({ ...f, code: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(codes).map(([c, m]) => <SelectItem key={c} value={c}>{c} — {m} $</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </CreateDialog>
    }>
      <Table className="clin-table"><TableHeader><TableRow><TableHead>Demande</TableHead><TableHead>Codes</TableHead><TableHead>Total</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => <TableRow key={r.id} className="h-9"><TableCell className="font-mono text-xs">{r.numero}</TableCell><TableCell className="text-xs">{r.lignes?.map((l) => l.code).join(", ")}</TableCell><TableCell className="font-mono">{r.total} $</TableCell><TableCell><Badge variant="outline">{r.statut}</Badge></TableCell></TableRow>)}</TableBody></Table>
    </Section>
  );
}

// ===== Partie H =====
function PartieH() {
  const demandes = useDemandes();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ demande_id: "", prescripteur: "", message: "", criticite: "normale" });
  const load = () => api.get("/parties/h/notifications").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/parties/h/notifications", f); toast.success("Notification envoyée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const escalade = async (id, niveau) => { try { await api.patch(`/parties/h/notifications/${id}/escalade`, { niveau }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const accuser = async (id) => { try { await api.patch(`/parties/h/notifications/${id}/accuser`); toast.success("Accusé de réception"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Section title="Partie H — Notifications aux médecins prescripteurs" action={
      <CreateDialog label="Notifier un médecin prescripteur" testid="new-notification-button" onSubmit={submit}>
        <div className="space-y-3">
          <DemandeSelect demandes={demandes} value={f.demande_id} onChange={(v) => setF({ ...f, demande_id: v })} />
          <Input placeholder="Médecin prescripteur" value={f.prescripteur} onChange={(e) => setF({ ...f, prescripteur: e.target.value })} />
          <Textarea placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <DictationButton value={f.message || ""} onChange={(v) => setF((x) => ({ ...x, message: v }))} testId="dictation-notification-message" />
          <Select value={f.criticite} onValueChange={(v) => setF({ ...f, criticite: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="normale">Normale</SelectItem><SelectItem value="critique">Critique</SelectItem><SelectItem value="cancer">Cancer (blocage)</SelectItem></SelectContent>
          </Select>
        </div>
      </CreateDialog>
    }>
      <Table className="clin-table"><TableHeader><TableRow><TableHead>Demande</TableHead><TableHead>Médecin prescripteur</TableHead><TableHead>Criticité</TableHead><TableHead>Escalade</TableHead><TableHead>Accusé</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((r) => (
          <TableRow key={r.id} className="h-9">
            <TableCell className="font-mono text-xs">{r.numero}</TableCell>
            <TableCell className="text-xs">{r.prescripteur}</TableCell>
            <TableCell><Badge variant="outline" className={r.criticite === "cancer" ? "bg-red-50 text-red-700 border-red-200" : r.criticite === "critique" ? "bg-amber-50 text-amber-800 border-amber-200" : ""}>{r.criticite}</Badge></TableCell>
            <TableCell><div className="flex gap-1">{["N1", "N2", "N3"].map((n) => <button key={n} onClick={() => escalade(r.id, n)} className={`text-[10px] px-1.5 py-0.5 rounded ${r.niveau_escalade === n ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600"}`}>{n}</button>)}</div></TableCell>
            <TableCell>{r.accuse_reception ? <Badge className="bg-emerald-600 text-[10px]">Reçu</Badge> : <span className="text-xs text-slate-400">En attente</span>}</TableCell>
            <TableCell>{!r.accuse_reception && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => accuser(r.id)} data-testid={`accuser-${r.numero}`}>Accuser</Button>}</TableCell>
          </TableRow>
        ))}</TableBody></Table>
    </Section>
  );
}

function Section({ title, action, children }) {
  return (
    <Card className="p-4 border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head text-lg font-semibold text-slate-800">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

export default function Parties() {
  return (
    <div data-testid="parties-page">
      <PageHeader title="Parties B → H" subtitle="Consultations, avis d'expert, gardes, double validation, facturation, notifications." />
      <Tabs defaultValue="b">
        <TabsList>
          <TabsTrigger value="b" data-testid="tab-partie-b">B · Consultations</TabsTrigger>
          <TabsTrigger value="c" data-testid="tab-partie-c">C · Avis d'expert</TabsTrigger>
          <TabsTrigger value="d" data-testid="tab-partie-d">D · Gardes</TabsTrigger>
          <TabsTrigger value="e" data-testid="tab-partie-e">E · Double validation</TabsTrigger>
          <TabsTrigger value="f" data-testid="tab-partie-f">F · Facturation L4E</TabsTrigger>
          <TabsTrigger value="h" data-testid="tab-partie-h">H · Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value="b" className="mt-4"><PartieB /></TabsContent>
        <TabsContent value="c" className="mt-4"><PartieC /></TabsContent>
        <TabsContent value="d" className="mt-4"><PartieD /></TabsContent>
        <TabsContent value="e" className="mt-4"><PartieE /></TabsContent>
        <TabsContent value="f" className="mt-4"><PartieF /></TabsContent>
        <TabsContent value="h" className="mt-4"><PartieH /></TabsContent>
      </Tabs>
    </div>
  );
}
